import {
  Box,
  MenuItem,
  Typography,
  Select,
  TextField,
  Checkbox,
  FormControl,
  Autocomplete,
  Radio,
} from "@mui/material";
import "./LoadoutFilter.css";
import { useEffect, useRef, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { ConfigState, FiltersConfig, VehicleOption } from "./types";
import { useDispatch } from "react-redux";
import DateRangeWisePicker from "../../dms_common/DateRangeWisePicker";
import { logAnalyticsEvent } from "../../../firebase";
import { getDayEndStatusFromSession } from "../../dms_common/DayEndUtils";

interface LoadoutFilterProps {
  filters: {
    key: string;
    label: string;
    options: (string | VehicleOption)[];
    state: string | string[] | VehicleOption | undefined;
    setState: React.Dispatch<React.SetStateAction<any>>;
    isNecessary: boolean;
    multiSelect: boolean;
  }[];
  dateChangeHandler: (startDate: Dayjs | null, endDate: Dayjs | null) => void;
  deliveryStartDate: Dayjs | null;
  deliveryEndDate: Dayjs | null;
  loadoutConfig: ConfigState;
  loadoutStatus?: string;
  setSnackbarMessage?: (message: string) => void;
  setSnackbarOpen?: (open: boolean) => void;
  loadNumber?: string | null;
}

export default function LoadoutFilter({
  filters,
  deliveryStartDate,
  deliveryEndDate,
  dateChangeHandler,
  loadoutConfig,
  loadoutStatus,
  setSnackbarMessage,
  setSnackbarOpen,
  loadNumber
}: LoadoutFilterProps) {
  const salesRepOrRouteOptions =
    filters.find((filter) => filter.key === "salesRepOrRoute")?.options || [];

  const deliveryRouteOptions =
    filters.find((filter) => filter.key === "deliveryRoute")?.options || [];

  const originalSalesRepOrRouteOptions = useRef<Array<string | VehicleOption>>([
    ...salesRepOrRouteOptions,
  ]);

  const originalDeliveryRouteOptions = useRef<Array<string | VehicleOption>>([
    ...deliveryRouteOptions,
  ]);

  const [sortedSalesRepOrRouteOptions, setSortedSalesRepOrRouteOptions] =
    useState<Array<string | VehicleOption>>([...salesRepOrRouteOptions]);

  const [sortedDeliveryRouteOptions, setSortedDeliveryRouteOptions] = useState<
    Array<string | VehicleOption>
  >([...deliveryRouteOptions]);

  useEffect(() => {
    if (salesRepOrRouteOptions.length > 0) {
      originalSalesRepOrRouteOptions.current = [...salesRepOrRouteOptions];
      setSortedSalesRepOrRouteOptions([...salesRepOrRouteOptions]);
    }
  }, [salesRepOrRouteOptions]);

  useEffect(() => {
    if (deliveryRouteOptions.length > 0) {
      originalDeliveryRouteOptions.current = [...deliveryRouteOptions];
      setSortedDeliveryRouteOptions([...deliveryRouteOptions]);
    }
  }, [deliveryRouteOptions]);

  const [salesRepSearchQuery, setSalesRepSearchQuery] = useState<string>("");
  const [deliveryRouteSearchQuery, setDeliveryRouteSearchQuery] =
    useState<string>("");

  const sortMultipleOptions = (filter: any) => {
    const isSalesRepOrRouteFilter = filter.key === "salesRepOrRoute";
    const isDeliveryRouteFilter = filter.key === "deliveryRoute";

    if (isSalesRepOrRouteFilter) {
      const selectedOptions: Array<string | VehicleOption> = [];
      const unselectedOptions: Array<string | VehicleOption> = [];

      originalSalesRepOrRouteOptions.current.forEach((option) => {
        const optionValue =
          typeof option === "string"
            ? option
            : (option as VehicleOption).vehicleNumber;

        if (Array.isArray(filter.state) && filter.state.includes(optionValue)) {
          selectedOptions.push(option);
        } else {
          unselectedOptions.push(option);
        }
      });

      setSortedSalesRepOrRouteOptions([
        ...selectedOptions,
        ...unselectedOptions,
      ]);
    }

    if (isDeliveryRouteFilter) {
      const selectedOptions: Array<string | VehicleOption> = [];
      const unselectedOptions: Array<string | VehicleOption> = [];

      originalDeliveryRouteOptions.current.forEach((option) => {
        const optionValue =
          typeof option === "string"
            ? option
            : (option as VehicleOption).vehicleNumber;

        if (Array.isArray(filter.state) && filter.state.includes(optionValue)) {
          selectedOptions.push(option);
        } else {
          unselectedOptions.push(option);
        }
      });

      setSortedDeliveryRouteOptions([...selectedOptions, ...unselectedOptions]);
    }
  };

  // Function to restore original order
  const restoreOriginalOrder = (filter?: any) => {
    const isSalesRepOrRouteFilter = filter?.key === "salesRepOrRoute";
    const isDeliveryRouteFilter = filter?.key === "deliveryRoute";

    if (isSalesRepOrRouteFilter) {
      setSortedSalesRepOrRouteOptions([
        ...originalSalesRepOrRouteOptions.current,
      ]);
    }

    if (isDeliveryRouteFilter) {
      setSortedDeliveryRouteOptions([...originalDeliveryRouteOptions.current]);
    }
  };

  // Function to reorder options based on selected state
  const getReorderedOptions = (
    filter: any,
    baseOptions: Array<
      | { type: "selectAll"; value: string; label: string }
      | string
      | VehicleOption
    >
  ) => {
    return baseOptions;
  };
  const dispatch = useDispatch();
  const dayEndObj = getDayEndStatusFromSession();

  let initialStartDate;
  let initialEndDate;
  if (dayEndObj && dayEndObj.isDayEndInProgress  && (!loadNumber)){
    initialStartDate = dayjs(dayEndObj.startDate).add(loadoutConfig?.maxDeliveryDateOffset ?? 0, "days")
    initialEndDate = dayjs(dayEndObj.endDate).add(loadoutConfig?.maxDeliveryDateOffset ?? 0, "days")
  } else {
    initialStartDate = deliveryStartDate
    initialEndDate = deliveryEndDate
  }

  const [startDate, setStartDate] = useState<Dayjs | null>(deliveryStartDate);
  const [endDate, setEndDate] = useState<Dayjs | null>(deliveryEndDate);
  const prevVehicleStateRef = useRef<VehicleOption | undefined>(undefined);
  const previousSelectAllStateRef = useRef<string[]>([]);
  const selectDropdownRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);

  const updateLoadoutInfo = (value: Record<string, any>) => {
    if (dayEndObj && dayEndObj.isDayEndInProgress) return
    dispatch({ type: "SET_LOADOUT", payload: value });
  };

  useEffect(() => {
    const salesmanFilter = filters.find((filter) => filter.key === "salesman");
    if (salesmanFilter && salesmanFilter.state) {
      logAnalyticsEvent("delivery_loadout_preseller_salesman_selected", {
        distributor_id: JSON.parse(localStorage.authContext).user.loginId,
        loadout_salesman_selected: salesmanFilter.state,
      });
      updateLoadoutInfo({ SalesMan: salesmanFilter.state });
    }
  }, [filters]);

  useEffect(() => {
    const salesRepOrRouteFilter = filters.find(
      (filter) => filter.key === "salesRepOrRoute"
    );
    if (salesRepOrRouteFilter && Array.isArray(salesRepOrRouteFilter.state)) {
      logAnalyticsEvent(
        `delivery_loadout_preseller_${
          loadoutConfig.presellerRouteMaintained ? "delivery_route" : "salesrep"
        }_filter`,
        {
          distributor_id: JSON.parse(localStorage.authContext).user.loginId,
          [loadoutConfig.presellerRouteMaintained
            ? "loadout_delivery_route_selected"
            : "loadout_salesrep_selected"]: salesRepOrRouteFilter.state,
        }
      );
      updateLoadoutInfo({ salesRepOrRoute: salesRepOrRouteFilter.state });
    }
  }, [filters, loadoutConfig.presellerRouteMaintained]);

  useEffect(() => {
    const salesRouteFilter = filters.find(
      (filter) => filter.key === "deliveryRoute"
    );
    if (salesRouteFilter && salesRouteFilter.state) {
      logAnalyticsEvent("delivery_loadout_preseller_sales_route_selected", {
        distributor_id: JSON.parse(localStorage.authContext).user.loginId,
        loadout_sales_route_selected: salesRouteFilter.state,
      });
      updateLoadoutInfo({ selectedRoute: salesRouteFilter.state });
    }
  }, [filters]);

  useEffect(() => {
    const vehicleFilter = filters.find((filter) => filter.key === "vehicle");
    if (vehicleFilter && vehicleFilter.state) {
      const currentVehicle = vehicleFilter.state as VehicleOption;
      if (
        JSON.stringify(currentVehicle) !==
        JSON.stringify(prevVehicleStateRef.current)
      ) {
        logAnalyticsEvent("delivery_loadout_preseller_vehicle_selected", {
          distributor_id: JSON.parse(localStorage.authContext).user.loginId,
          loadout_vehicle_selected: currentVehicle,
        });
        updateLoadoutInfo({ Vehicle: currentVehicle });
        // Update the ref with the new vehicle state
        prevVehicleStateRef.current = currentVehicle;
      }
    }
  }, [filters, logAnalyticsEvent, updateLoadoutInfo]);

  useEffect(() => {
    const start: Dayjs | null = startDate ? startDate.startOf("day") : null;
    const end: Dayjs | null = endDate ? endDate.endOf("day") : null;
    dateChangeHandler(start, end);
  }, [startDate, endDate]);

  useEffect(() => {
    if (deliveryStartDate && !startDate?.isSame(deliveryStartDate)) {
      setStartDate(deliveryStartDate);
    }

    if (deliveryEndDate && !endDate?.isSame(deliveryEndDate)) {
      setEndDate(deliveryEndDate);
    }
  }, [deliveryStartDate, deliveryEndDate]);

  const dropdownStyling = {
    popper: {
      sx: {
        marginTop: "6px !important",
        borderRadius: "6px !important",
        border: "1px solid #EBEBEB",
        "& .MuiAutocomplete-option": {
          fontSize: "13px !important",
          color: "#606060",
          fontWeight: 500,
          letterSpacing: 0,
          padding: "0 !important",
        },
        "& .MuiAutocomplete-listbox": {
          maxHeight: "170px",
          padding: "0 !important",
          borderRadius: "6px !important",
        },
        "& .MuiPaper-elevation1": {
          borderRadius: "6px !important",
          boxShadow: "none",
        },
      },
    },
  };

  const multiSelectDropdown = (filter: any) => {
    const isDisabled =
      loadoutStatus === "IN-TRANSIT" || loadoutStatus === "DELIVERED";

    const deliveryRouteFilter = filters.find((f) => f.key === "deliveryRoute");
    const isSalesRepDisabled =
      loadoutConfig?.enableDeliveryRouteFilter &&
      filter.key === "salesRepOrRoute" &&
      (Array.isArray(deliveryRouteFilter?.state)
        ? deliveryRouteFilter?.state?.length === 0
        : true);

    const isSalesRepOrRouteFilter = filter.key === "salesRepOrRoute";
    const isDeliveryRouteFilter = filter.key === "deliveryRoute";

    const currentOptions = isSalesRepOrRouteFilter
      ? sortedSalesRepOrRouteOptions
      : isDeliveryRouteFilter
      ? sortedDeliveryRouteOptions
      : [];

    const currentSearchQuery = isSalesRepOrRouteFilter
      ? salesRepSearchQuery
      : isDeliveryRouteFilter
      ? deliveryRouteSearchQuery
      : "";

    const normalizedQuery = currentSearchQuery.trim().toLowerCase();
    let filteredOptions =
      normalizedQuery.length === 0
        ? [...currentOptions]
        : currentOptions.filter((option) => {
            const optionLabel =
              typeof option === "string"
                ? option
                : (option as VehicleOption).vehicleNumber || "";
            return optionLabel.toLowerCase().includes(normalizedQuery);
          });

    // If status is IN-TRANSIT or DELIVERED, show only selected options
    if (isDisabled) {
      filteredOptions = filteredOptions.filter((option) => {
        const optionValue =
          typeof option === "string"
            ? option
            : (option as VehicleOption).vehicleNumber;
        return (
          Array.isArray(filter?.state) && filter?.state.includes(optionValue)
        );
      });
    }

    // Compose options with Select All, optional Search row, and the filtered results
    const optionsWithSelectAll: Array<
      | { type: "selectAll"; value: string; label: string }
      | string
      | VehicleOption
    > = getReorderedOptions(filter, 
      filteredOptions.length > 0
        ? [
            { type: "selectAll", value: "__SELECT_ALL__", label: "Select All" },
            ...filteredOptions,
          ]
        : filteredOptions
    );

    const handleSelectAllChange = (selectedValues: string[]) => {
      if (isDisabled) {
        return;
      }

      const hasSelectAll = selectedValues.includes("__SELECT_ALL__");
      const hasOtherOptions = selectedValues.some(
        (value) => value !== "__SELECT_ALL__"
      );
      const currentState = Array.isArray(filter?.state) ? filter?.state : [];
      const isAllCurrentlySelected =
        currentState.length === currentOptions.length;

      // Check if this is a "Select All" toggle action by comparing with previous state
      const wasSelectAllInPreviousState =
        previousSelectAllStateRef.current.includes("__SELECT_ALL__");
      const isSelectAllToggle = hasSelectAll && !wasSelectAllInPreviousState;

      // Store scroll position before state change if this is a "Select All" toggle
      if (isSelectAllToggle) {
        const menuElement =
          selectDropdownRef.current?.querySelector('[role="listbox"]');
        if (menuElement) {
          scrollPositionRef.current = menuElement.scrollTop;
        }
      }

      // Track the selection that will be committed so we can sort immediately
      let nextSelection: string[] = currentState;

      // Handle "Select All" toggle logic
      if (isSelectAllToggle) {
        if (isAllCurrentlySelected) {
          // If all are currently selected, deselect all
          nextSelection = [];
          filter?.setState && filter?.setState([]);
        } else {
          // If not all are selected, select all
          const allOptions = currentOptions
            .map((option) =>
              typeof option === "string"
                ? option
                : (option as VehicleOption).vehicleNumber
            )
            .filter((v): v is string => typeof v === "string" && v.length > 0);
          nextSelection = allOptions;
          filter?.setState && filter?.setState(allOptions);
        }

        // Restore scroll position after state update
        requestAnimationFrame(() => {
          const menuElement =
            selectDropdownRef.current?.querySelector('[role="listbox"]');
          if (menuElement && scrollPositionRef.current > 0) {
            menuElement.scrollTop = scrollPositionRef.current;
          }
        });
      } else if (hasSelectAll && hasOtherOptions) {
        // "Select All" + other options selected, select all options
        const allOptions = currentOptions
          .map((option) =>
            typeof option === "string"
              ? option
              : (option as VehicleOption).vehicleNumber
          )
          .filter((v): v is string => typeof v === "string" && v.length > 0);
        nextSelection = allOptions;
        filter?.setState && filter?.setState(allOptions);
      } else if (!hasSelectAll && hasOtherOptions) {
        // Only other options selected, remove "Select All" from selection
        const otherOptions = selectedValues
          .filter((value) => value !== "__SELECT_ALL__")
          .filter((v): v is string => typeof v === "string");
        nextSelection = otherOptions;
        filter?.setState && filter?.setState(otherOptions);
      } else if (hasSelectAll && !hasOtherOptions) {
        // Only "Select All" is selected, this means user clicked "Select All" when all were already selected
        // So we should deselect all
        nextSelection = [];
        filter?.setState && filter?.setState([]);
        // Restore original order when all items are deselected
        restoreOriginalOrder(filter);
      } else {
        // Nothing selected
        nextSelection = [];
        filter?.setState && filter?.setState([]);
        // Restore original order when nothing is selected
        restoreOriginalOrder(filter);
      }

      // Additional check: if "Select All" is clicked and all items are already selected, deselect all
      if (
        hasSelectAll &&
        isAllCurrentlySelected &&
        selectedValues.length === currentOptions.length + 1
      ) {
        console.log("Direct deselect all triggered");
        nextSelection = [];
        filter?.setState && filter?.setState([]);
        // Restore original order when all items are deselected
        restoreOriginalOrder(filter);
      }

      // Additional check: if "Select All" is clicked and no items are selected, select all
      if (hasSelectAll && !hasOtherOptions && currentState.length === 0) {
        console.log("Direct select all triggered");
        const allOptions = currentOptions
          .map((option) =>
            typeof option === "string"
              ? option
              : (option as VehicleOption).vehicleNumber
          )
          .filter((v): v is string => typeof v === "string" && v.length > 0);
        nextSelection = allOptions;
        filter?.setState && filter?.setState(allOptions);
      }

      previousSelectAllStateRef.current = selectedValues;

      // Do not reorder immediately; let onClose decide when to sort (restores previous UX)
    };

    const isAllSelected =
      Array.isArray(filter?.state) &&
      filter?.state.length === currentOptions.length &&
      currentOptions.length > 0;
    const isIndeterminate =
      Array.isArray(filter?.state) &&
      filter?.state.length > 0 &&
      filter?.state.length < currentOptions.length;

    return (
      <Box
        key={filter?.label}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flexWrap: "nowrap",
          width: "auto",
          flexGrow: 0.6,
          maxWidth: loadoutConfig?.presellerRouteMaintained ? "290px" : "275px",
        }}
      >
        <Typography className="labelText">
          {filter?.label}
          {filter?.isNecessary && <span style={{ color: "red" }}>*</span>}:
        </Typography>
        <FormControl sx={{ width: "max-content", flexGrow: 1 }}>
          <Select
            ref={selectDropdownRef}
            size="small"
            multiple
            disabled={isSalesRepDisabled}
            value={Array.isArray(filter?.state) ? filter?.state : []}
            onChange={(e) => {
              const selectedValues = e.target.value as string[];
              handleSelectAllChange(selectedValues);
            }}
            onClose={() => {
              // Sort multipleOptions when dropdown closes
              sortMultipleOptions(filter);
              // Reset search on close so full list is shown next open
              if (isSalesRepOrRouteFilter) {
                setSalesRepSearchQuery("");
              } else if (isDeliveryRouteFilter) {
                setDeliveryRouteSearchQuery("");
              }
            }}
            onOpen={() => {
              if (Array.isArray(filter?.state) && filter?.state.length > 0) {
                sortMultipleOptions(filter);
              }
            }}
            onClick={() => {
              if (
                loadoutConfig?.enableDeliveryRouteFilter &&
                isSalesRepDisabled &&
                setSnackbarMessage &&
                setSnackbarOpen
              ) {
                setSnackbarMessage(
                  "Please select Route ID to proceed with Salesrep Selection"
                );
                setSnackbarOpen(true);
              }
            }}
            displayEmpty
            inputProps={{ "aria-label": "Without label" }}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return (
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: "13px",
                      color: "#a2a2a2",
                      mt: "2px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      width: "100%",
                      padding: "3.5px 0",
                    }}
                  >
                    Select here
                  </Typography>
                );
              }

              const firstItem = selected[0];
              const othersCount = selected.length - 1;
              const displayText = `${
                firstItem.length > 7 && othersCount > 0
                  ? firstItem.slice(0, 7) + "..."
                  : firstItem.length > 16
                  ? firstItem.slice(0, 16) + "..."
                  : firstItem
              } ${othersCount > 0 ? `& ${othersCount} others` : ""}`;

              return (
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    width: "100%",
                  }}
                >
                  {displayText}
                </Typography>
              );
            }}
            className="select-loadout"
            sx={{
              display: "flex",
              flexDirection: "row",
              height: "35.69px !important",
              opacity:
                loadoutConfig?.enableDeliveryRouteFilter && isSalesRepDisabled
                  ? 0.6
                  : 1,
              cursor:
                loadoutConfig?.enableDeliveryRouteFilter && isSalesRepDisabled
                  ? "not-allowed"
                  : "pointer",
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight:
                    isSalesRepOrRouteFilter || isDeliveryRouteFilter
                      ? "330px"
                      : "150px",
                  overflowY: "auto",
                  mt: 1,
                  borderRadius: "8px !important",
                  boxShadow: "none",
                  border: "1px solid #EBEBEB",
                  padding: "0 !important",
                },
              },
              MenuListProps: {
                sx: {
                  padding: "0",
                },
                onScroll: (e) => {
                  e.stopPropagation();
                },
              },
              autoFocus: false,
              disableAutoFocus: true,
              disableAutoFocusItem: true,
            }}
          >
            {/* Optional search input row for Sales Rep filter */}
            {(isSalesRepOrRouteFilter || isDeliveryRouteFilter) && 
             currentOptions.length > 0 && (
              <MenuItem
                tabIndex={-1}
                disableRipple
                sx={{
                  padding: 0,
                  mt: 0.5,
                  backgroundColor: "transparent",
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                  "&.Mui-focusVisible": {
                    backgroundColor: "transparent",
                  },
                  "&:focus": {
                    backgroundColor: "transparent",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "transparent",
                  },
                }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <Box sx={{ width: "100%", p: "2px 6px 0px 6px" }}>
                  <TextField
                    size="small"
                    placeholder="Search"
                    fullWidth
                    value={currentSearchQuery}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      if (isSalesRepOrRouteFilter) {
                        setSalesRepSearchQuery(e.target.value);
                      } else if (isDeliveryRouteFilter) {
                        setDeliveryRouteSearchQuery(e.target.value);
                      }
                    }}
                    inputProps={{
                      style: { fontSize: 12, fontWeight: 500 },
                      "aria-label": isSalesRepOrRouteFilter
                        ? "Sales rep search"
                        : "Delivery route search",
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#FFFFFF",
                        borderRadius: "8px",
                        transition:
                          "background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease",
                      },
                      "& .MuiOutlinedInput-input::placeholder": {
                        fontWeight: 500,
                        letterSpacing: 0,
                        color: "#A7A7A7",
                        opacity: 1,
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#D4D4D4",
                        borderWidth: "1px !important",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#D4D4D4",
                        borderWidth: "1px !important",
                      },
                      "& .MuiOutlinedInput-root:hover": {
                        backgroundColor: "#FFFFFF",
                      },
                      "& .MuiOutlinedInput-root.Mui-focused": {
                        backgroundColor: "#FFFFFF",
                      },
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: "#D4D4D4",
                          borderWidth: "1.75px !important",
                        },
                    }}
                  />
                </Box>
              </MenuItem>
            )}
            {optionsWithSelectAll.length === 0 ? (
              <MenuItem disabled>
                <Typography
                  sx={{
                    fontSize: "16px",
                    color: "#000000 !important",
                    fontWeight: 400,
                    letterSpacing: 0,
                    padding: "8px 5px !important",
                  }}
                >
                  No options
                </Typography>
              </MenuItem>
            ) : (
              optionsWithSelectAll.map((option, index) => {
                const isSelectAll =
                  typeof option === "object" &&
                  "type" in option &&
                  option.type === "selectAll";
                const optionValue = isSelectAll
                  ? (option as any).value
                  : typeof option === "string"
                  ? option
                  : (option as VehicleOption).vehicleNumber;
                const optionLabel = isSelectAll
                  ? (option as any).label
                  : typeof option === "string"
                  ? option
                  : (option as VehicleOption).vehicleNumber;

                let isSelected = false;
                if (isSelectAll) {
                  isSelected = isAllSelected;
                } else {
                  isSelected =
                    Array.isArray(filter?.state) &&
                    filter?.state.includes(optionValue);
                }

                return (
                  <MenuItem
                    key={index}
                    value={optionValue}
                    disabled={isDisabled}
                    sx={{
                      width: filters?.length === 3 ? "94%" : "172px",
                      wordWrap: "break-word",
                      padding: "0 !important",
                      letterSpacing: 0,
                      margin: "5px 0px 3px 5px",
                      background: "transparent !important",
                      display: "flex",
                      flexDirection: "column",
                      borderBottom: isSelectAll
                        ? "1.8px solid #EBEBEB"
                        : "none",
                      "&.Mui-disabled": {
                        opacity: 1,
                        color: "inherit",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        backgroundColor: isSelected
                          ? "#fafafa !important"
                          : "transparent",
                        borderRadius: "6px",
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={isSelected}
                        indeterminate={isSelectAll && isIndeterminate}
                        disabled={isDisabled}
                        sx={{
                          "&.Mui-checked": {
                            color: "#00c6b2",
                          },
                          "&.MuiCheckbox-indeterminate": {
                            color: "#00c6b2",
                          },
                          "&.Mui-disabled": {
                            color: isSelected ? "#00c6b2" : "#bdbdbd",
                          },
                        }}
                      />
                      <Typography
                        sx={{
                          whiteSpace: "normal",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          color: isSelectAll
                            ? "#606060"
                            : isSelected
                            ? "#606060"
                            : "#909090",
                          fontSize: "13px",
                          width: "100%",
                          fontWeight: isSelectAll ? 600 : 500,
                          wordWrap: "break-word",
                        }}
                      >
                        {optionLabel}
                      </Typography>
                    </Box>
                    {index < optionsWithSelectAll.length - 1 &&
                      !isSelectAll && (
                        <Box
                          sx={{
                            width: "92%",
                            borderBottom: "1.8px dashed #EBEBEB",
                            margin: "auto",
                            mt: "3px",
                          }}
                        />
                      )}
                  </MenuItem>
                );
              })
            )}
            {(isSalesRepOrRouteFilter || isDeliveryRouteFilter) &&
              currentOptions.length > 0 &&
              normalizedQuery.length > 0 &&
              filteredOptions.length === 0 && (
                <MenuItem disabled>
                  <Typography
                    sx={{ fontSize: "13px", color: "#909090", fontWeight: 500 }}
                  >
                    No results
                  </Typography>
                </MenuItem>
              )}
          </Select>
        </FormControl>
      </Box>
    );
  };

  let maxDate;
  let minDate;
  let isDateSelectionDisabled = false;

  if (dayEndObj && dayEndObj.isDayEndInProgress && (!(loadoutStatus === "IN-TRANSIT" || loadoutStatus === "DELIVERED"))){
    minDate = dayjs(dayEndObj.startDate).add(loadoutConfig?.maxDeliveryDateOffset ?? 0,"days");
    maxDate = dayjs(dayEndObj.endDate).add(loadoutConfig?.maxDeliveryDateOffset ?? 0,"days");
    isDateSelectionDisabled = true;
  } else {
    minDate = dayjs().subtract(
      loadoutConfig?.minDeliveryDateOffset ?? 15,
      "days"
    );
    maxDate = dayjs().add(
      loadoutConfig?.maxDeliveryDateOffset ?? 15,
      "days"
    );
    isDateSelectionDisabled = loadoutStatus === "IN-TRANSIT" || loadoutStatus === "DELIVERED"
  }


  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        justifyContent: "space-between",
        mt: "7px",
        gap: "40px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: "15px",
          flexGrow: "0.95",
        }}
      >
        {filters?.map((filter, index) =>
          filter?.multiSelect ? (
            multiSelectDropdown(filter)
          ) : (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                flexWrap: "nowrap",
                width: "auto",
                flexGrow: 1,
                maxWidth: loadoutConfig?.presellerRouteMaintained
                  ? "290px"
                  : "275px",
              }}
            >
              <Typography className="labelText">
                {filter?.label}
                {filter?.isNecessary && <span style={{ color: "red" }}>*</span>}
                :
              </Typography>
              <Autocomplete
                size="small"
                options={filter.options}
                value={
                  filter.key === "vehicle"
                    ? typeof filter.state === "object" &&
                      "vehicleNumber" in filter.state
                      ? filter.state
                      : undefined
                    : typeof filter.state === "string"
                    ? filter.state
                    : undefined
                }
                onChange={(event, newValue) => {
                  if (
                    loadoutStatus === "IN-TRANSIT" ||
                    loadoutStatus === "DELIVERED"
                  ) {
                    return;
                  }
                  if (filter.key === "vehicle") {
                    const selectedVehicle = filter.options.find(
                      (option) =>
                        typeof option !== "string" &&
                        option.vehicleNumber ===
                          (newValue as VehicleOption)?.vehicleNumber
                    );
                    if (selectedVehicle) {
                      filter.setState(selectedVehicle);
                    } else {
                      filter.setState(undefined);
                    }
                  } else {
                    filter.setState && filter.setState(newValue || "");
                  }
                }}
                getOptionLabel={(option) => {
                  if (typeof option === "string") {
                    return option;
                  }
                  if (
                    typeof option === "object" &&
                    option &&
                    "vehicleNumber" in option
                  ) {
                    return option.vehicleNumber || "";
                  }
                  return "";
                }}
                renderInput={(params) => {
                  const inputValue =
                    filter.key === "vehicle" &&
                    typeof filter.state === "object" &&
                    "vehicleNumber" in filter.state
                      ? filter.state.vehicleNumber
                      : params.inputProps.value;

                  return (
                    <TextField
                      {...params}
                      size="small"
                      className="select-loadout"
                      placeholder="Select here"
                      inputProps={{
                        ...params.inputProps,
                        value: inputValue,
                        "aria-label": "Without label",
                      }}
                      sx={{
                        "& .MuiInputBase-input::placeholder": {
                          fontWeight: 500,
                          letterSpacing: 0,
                        },
                      }}
                    />
                  );
                }}
                renderOption={(props, option, state) => {
                  const isLastOption =
                    filter.options[filter.options.length - 1] === option;
                  const isSelected =
                    filter.key === "vehicle"
                      ? typeof filter.state === "object" &&
                        "vehicleNumber" in filter.state &&
                        filter.state.vehicleNumber ===
                          (typeof option === "object" &&
                          "vehicleNumber" in option
                            ? option.vehicleNumber
                            : "")
                      : filter.state ===
                        (typeof option === "string"
                          ? option
                          : option.vehicleNumber || "");

                  return (
                    <li
                      {...props}
                      key={
                        typeof option === "object" &&
                        "id" in option &&
                        option.id
                          ? option.id
                          : state.index
                      }
                      style={{
                        backgroundColor: "transparent",
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "95%",
                          margin: "5px 0px 3px 0px",
                          backgroundColor: isSelected
                            ? "#fafafa"
                            : "transparent",
                          borderRadius: "6px",
                          padding: "5px 0",
                        }}
                      >
                        <Radio
                          checked={isSelected}
                          sx={{
                            padding: "0 8px",
                            "&.Mui-checked": {
                              color: "#00c6b2",
                            },
                            "& .MuiSvgIcon-root": { fontSize: 18 },
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: 500,
                            color: "#606060",
                            flexGrow: 1,
                            whiteSpace: "normal",
                            overflow: "visible",
                            textOverflow: "clip",
                            pr: "3px",
                          }}
                        >
                          {typeof option === "string"
                            ? option
                            : option.vehicleNumber}
                        </Typography>
                      </Box>
                      {!isLastOption && (
                        <Box
                          sx={{
                            width: "90%",
                            borderBottom: "1.8px dashed #EBEBEB",
                            margin: "auto",
                          }}
                        />
                      )}
                    </li>
                  );
                }}
                sx={{
                  fontSize: "13px",
                  display: "flex",
                  flexGrow: 1,
                  "& .MuiAutocomplete-input": {
                    fontSize: "13px",
                    fontWeight: 600,
                  },
                }}
                disableClearable={true}
                componentsProps={dropdownStyling}
              />
            </Box>
          )
        )}
      </Box>
      {(loadoutConfig?.enableDeliveryDateAssignment ||
        loadoutConfig?.dateFilter) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            flexGrow: "0.05",
          }}
        >
          <Typography className="labelText">
            {loadoutConfig.filtersConfig.find(
              (config) =>
                config.key === "deliveryDate" || config.key === "creationTime"
            )?.label || "Delivery Date"}
            <span style={{ color: "red" }}>*</span>:
          </Typography>
          <Box sx={{ ml: "12px" }}>
            <DateRangeWisePicker
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              disabled={isDateSelectionDisabled}
              minDate={minDate}
              maxDate={maxDate}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}
